import React, { useState, useEffect } from 'react';

export function NotificationSettingsModal({ isOpen, onClose }) {
  const [settings, setSettings] = useState({
    email_notifications: true,
    sms_notifications: false,
    event_updates: true,
    registration_reminders: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchNotificationSettings();
    }
  }, [isOpen]);

  const fetchNotificationSettings = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch('/api/users/notification-settings', {
        headers: {
          'X-Session-Token': sessionToken,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings || settings);
      }
    } catch (err) {
      console.error('Error fetching notification settings:', err);
    }
  };

  const handleChange = (e) => {
    const { name, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await fetch('/api/users/notification-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken,
        },
        body: JSON.stringify({ settings }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update notification settings');
      }

      setSuccess('Notification settings updated successfully!');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Notification Settings</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={loading}
            />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="email_notifications"
                  name="email_notifications"
                  checked={settings.email_notifications}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label className="form-check-label" htmlFor="email_notifications">
                  Email Notifications
                </label>
                <small className="d-block text-muted">
                  Receive email updates about events and registrations
                </small>
              </div>

              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="sms_notifications"
                  name="sms_notifications"
                  checked={settings.sms_notifications}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label className="form-check-label" htmlFor="sms_notifications">
                  SMS Notifications
                </label>
                <small className="d-block text-muted">
                  Receive SMS messages for important updates
                </small>
              </div>

              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="event_updates"
                  name="event_updates"
                  checked={settings.event_updates}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label className="form-check-label" htmlFor="event_updates">
                  Event Updates
                </label>
                <small className="d-block text-muted">
                  Get notified about new events and changes
                </small>
              </div>

              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="registration_reminders"
                  name="registration_reminders"
                  checked={settings.registration_reminders}
                  onChange={handleChange}
                  disabled={loading}
                />
                <label className="form-check-label" htmlFor="registration_reminders">
                  Registration Reminders
                </label>
                <small className="d-block text-muted">
                  Receive reminders before registered events
                </small>
              </div>

              <hr className="my-4" />

              <div className="d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Settings'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
